// @flow

import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import ErrorPagePortal from "buba-components/src/views/ErrorPagePortal";
import { assertions } from "buba-components";

import LegalPagePortal from "buba-components/src/views/LegalPagePortal";
import BankInquiryDashboardNew from "../Dashboard/BankInquiryDashboardNew";
import BankInquiryDetails from "../Details/BankInquiryDetails";
import BankInquiryDocumentsNew from "../Documents/BankInquiryDocumentsNew";
import CustomerInquiryDashboard from "../Dashboard/CustomerInquiryDashboard";
import CustomerInquiryDetails from "../Details/CustomerInquiryDetails";
import CustomerInquiryDocuments from "../Documents/CustomerInquiryDocuments";
import InquiriesList from "../InquiriesList";
import InquiriesListV2 from "../InquiriesList/InquiriesListV2";
import Login from "../Login";
import Logout from "../Logout";
import ForgottenPassword from "../ForgottenPassword";
import UserData from "../UserData";
import NewPassword from "../NewPassword";
import ActivateUser from "../ActivateUser";
import RefreshPassword from "../RefreshPassword";
import SsoCallback from "../SsoCallback/SsoCallback";

import {
  isLoggedIn as isLoggedInSelector,
  isPasswordExpired as isPasswordExpiredSelector
} from "../../store/user/selectors";
import paths from "../../data/paths";
import BankInquiryApplication from "../BankInquiryApplication/index";

const PathCheckingRoute = (props: {
  computedMatch: {
    params: { id: string }
  }
}) => {
  const {
    computedMatch: { params }
  } = props;
  const { isPositiveInteger } = assertions;

  const parsedId = Number(params.id);
  const shouldShowAppRoute = !params.id || isPositiveInteger(parsedId);

  return shouldShowAppRoute ? <Route {...props} /> : <Redirect to={paths.error404} />;
};

const GuardedRoute = connect(state => ({
  isLoggedIn: isLoggedInSelector(state),
  isPasswordExpired: isPasswordExpiredSelector(state)
}))(({ isLoggedIn, isPasswordExpired, ...props }) => {
  const {
    location: { search, pathname }
  } = props;

  if (isLoggedIn) {
    if (isPasswordExpired) {
      return (
        <Redirect
          to={{
            pathname: paths.refreshPassword,
            search,
            state: { from: pathname }
          }}
        />
      );
    }

    return PathCheckingRoute(props);
  }

  return (
    <Redirect
      to={{
        pathname: paths.login,
        search,
        state: { from: pathname }
      }}
    />
  );
});

const OnlyAuthorizedRoute = connect(state => ({
  isLoggedIn: isLoggedInSelector(state)
}))(({ isLoggedIn, ...props }) =>
  isLoggedIn ? (
    PathCheckingRoute(props)
  ) : (
    <Redirect
      to={{
        pathname: paths.login,
        search: props.location.search,
        state: { from: props.location.pathname }
      }}
    />
  )
);

const OnlyUnauthorizedRoute = connect(state => ({
  isLoggedIn: isLoggedInSelector(state)
}))(({ isLoggedIn, location: { state }, ...props }) => {
  const routeToRedirect = (state && state.from) || paths.bankInquiriesListNew;

  return isLoggedIn ? <Redirect to={routeToRedirect} /> : <Route {...props} />;
});

const RefreshPasswordRoute = connect(state => ({
  isLoggedIn: isLoggedInSelector(state),
  isPasswordExpired: isPasswordExpiredSelector(state)
}))(({ isLoggedIn, isPasswordExpired, ...props }) =>
  isLoggedIn && isPasswordExpired ? (
    <Route {...props} />
  ) : (
    <Redirect to={paths.bankInquiriesListNew} />
  )
);

const Routes = () => (
  <Switch>
    <Route exact path="/" render={() => <Redirect to={paths.bankInquiriesListNew} />} />
    <OnlyUnauthorizedRoute exact path={paths.login} render={() => <Login />} />
    <OnlyUnauthorizedRoute exact path={paths.resetPassword} component={ForgottenPassword} />
    <OnlyUnauthorizedRoute exact path={paths.newPassword(":token")} component={NewPassword} />
    <OnlyUnauthorizedRoute exact path={paths.activateUser(":token")} component={ActivateUser} />
    <Route exact path={paths.ssoCallback} component={SsoCallback} />
    <RefreshPasswordRoute exact path={paths.refreshPassword} component={RefreshPassword} />
    <GuardedRoute exact path={paths.userData} component={UserData} />
    {/* TODO(v1-removal): These V1 redirect routes can be removed after the grace period ends */}
    <GuardedRoute
      exact
      path={paths.bankInquiryDashboard(":id")}
      render={({ match, location }) => (
        <Redirect to={paths.bankInquiryDashboardNew(match.params.id) + (location.search || "")} />
      )}
    />
    {/* V2 routes */}
    <GuardedRoute
      exact
      path={paths.bankInquiryDashboardNew(":id")}
      render={() => <BankInquiryDashboardNew />}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiriesListNew}
      component={() => <InquiriesListV2 type="bankAdvisor" />}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiryDetailsNew(":id")}
      render={() => <BankInquiryDetails isV2 />}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiryApplication(":id")}
      render={() => <BankInquiryApplication />}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiryDocumentsNew(":id")}
      render={() => <BankInquiryDocumentsNew />}
    />
    {/* V2 routes */}
    <GuardedRoute
      exact
      path={paths.customerInquiryDashboard(":id")}
      render={() => <CustomerInquiryDashboard />}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiryDetails(":id")}
      render={({ match, location }) => (
        <Redirect to={paths.bankInquiryDetailsNew(match.params.id) + (location.search || "")} />
      )}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiryDocuments(":id")}
      render={({ match, location }) => (
        <Redirect to={paths.bankInquiryDocumentsNew(match.params.id) + (location.search || "")} />
      )}
    />
    <GuardedRoute
      exact
      path={paths.bankInquiriesList}
      render={() => <Redirect to={paths.bankInquiriesListNew} />}
    />
    <GuardedRoute
      exact
      path={paths.customerInquiryDetails(":id")}
      render={() => <CustomerInquiryDetails />}
    />
    <GuardedRoute
      exact
      path={paths.customerInquiryDocuments(":id")}
      render={() => <CustomerInquiryDocuments />}
    />
    <GuardedRoute
      exact
      path={paths.customerInquiriesList}
      render={() => <InquiriesList type="customer" />}
    />
    <Route exact path={paths.error404} render={() => <ErrorPagePortal />} />
    <OnlyAuthorizedRoute exact path={paths.logout} component={Logout} />
    <Route
      exact
      path={paths.genderDisclaimer}
      render={() => <LegalPagePortal type="genderDisclaimer" />}
    />
    <Route
      exact
      path={paths.termsAndConditions}
      render={() => <LegalPagePortal type="termsAndConditions" />}
    />
    <Route
      exact
      path={paths.privacyPolicy}
      render={() => <LegalPagePortal type="privacyPolicy" />}
    />
    <Route exact path={paths.legalNotice} render={() => <LegalPagePortal type="legalNotice" />} />
    <Route
      exact
      path={paths.generalGuaranteeConditions}
      render={() => <LegalPagePortal type="generalGuaranteeConditions" />}
    />
  </Switch>
);

export default Routes;
